import { Box, Divider, Switch, Typography } from '@mui/material'
import { useCollectionViewContext } from './CollectionViewPageContext'
import { useDiRef } from '@/common/react/di-hooks'
import type { CollectionViewsService } from '@/objects/services/CollectionViewsService'
import { iconForType } from '@/objects/property-forms/property-forms-shared.tsx'
import type { PropertyDefinitionModel } from '@/core/gql/gql-models-override.ts'

export function ColumnsPopover() {
  const { viewSettings, objectType, collectionId } = useCollectionViewContext()
  const collectionViewsService = useDiRef<CollectionViewsService>('CollectionViewsService')
  const propDefs = objectType?.propertyDefinitions ?? []

  function isVisible(propDefId: number): boolean {
    const col = viewSettings?.columns.find((c) => c.propertyDefinitionId === propDefId)
    return col?.isVisible ?? false
  }

  function toggleColumn(propDefId: number) {
    collectionViewsService.updateColumn(collectionId, propDefId, {
      isVisible: !isVisible(propDefId),
    })
  }

  const visible = propDefs.filter((p) => isVisible(p.id))
  const hiddenRegular = propDefs.filter((p) => !isVisible(p.id) && !p.isMeta)
  const hiddenMeta = propDefs.filter((p) => !isVisible(p.id) && p.isMeta)

  function renderRow(p: PropertyDefinitionModel) {
    return (
      <Box
        key={p.id}
        onClick={() => toggleColumn(p.id)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0.5,
          py: 0.25,
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{iconForType(p.type, 'small')}</Box>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {p.name}
        </Typography>
        <Switch size="small" checked={isVisible(p.id)} tabIndex={-1} />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', minWidth: 260 }}>
      <Typography variant="subtitle2" sx={{ px: 0.5, pb: 0.5 }}>
        Visible
      </Typography>
      {visible.map(renderRow)}

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" sx={{ px: 0.5, pb: 0.5 }}>
        Hidden
      </Typography>
      {hiddenRegular.map(renderRow)}

      {hiddenMeta.length > 0 && hiddenRegular.length > 0 && <Divider sx={{ my: 1 }} />}
      {hiddenMeta.map(renderRow)}
    </Box>
  )
}
